import { Printer, FileImage, Files, LayoutGrid } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";

/**
 * Iter-130b — Layout chooser for multi-image continuity print.
 *
 * Intentionally tiny — three options, no advanced settings, no
 * "show metadata" toggles. The OSF philosophy is invisible interface;
 * the user picks the layout and prints. The actual print metadata
 * (Scene · Look · Notes) is fixed by spec.
 */
export default function PrintLayoutDialog({
    open,
    onOpenChange,
    count = 0,
    onPick,
}) {
    const pick = (layout) => {
        onPick(layout);
        onOpenChange(false);
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="print-layout-dialog"
                className="max-w-md bg-card border-white/10"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                        <Printer size={18} />
                        Print {count} image{count === 1 ? "" : "s"}
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Pick a layout — each sheet shows scene, look and notes
                        only. No project clutter.
                    </DialogDescription>
                </DialogHeader>
                <div className="mt-2 grid grid-cols-1 gap-2">
                    <button
                        type="button"
                        data-testid="print-layout-one"
                        onClick={() => pick("one")}
                        className="w-full text-left rounded-xl border border-white/10 bg-secondary/40 hover:bg-secondary/60 p-3 flex items-start gap-3 transition-colors"
                    >
                        <FileImage size={18} className="text-primary mt-0.5" />
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-white">
                                One per page
                            </p>
                            <p className="text-[12px] text-neutral-400 mt-0.5">
                                Full-bleed reference. Best for hero looks &
                                hand-offs.
                            </p>
                        </div>
                    </button>
                    <button
                        type="button"
                        data-testid="print-layout-two"
                        onClick={() => pick("two")}
                        className="w-full text-left rounded-xl border border-white/10 bg-secondary/40 hover:bg-secondary/60 p-3 flex items-start gap-3 transition-colors"
                    >
                        <Files size={18} className="text-primary mt-0.5" />
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-white">
                                Two per page
                            </p>
                            <p className="text-[12px] text-neutral-400 mt-0.5">
                                Side-by-side for before/after or continuity
                                comparison.
                            </p>
                        </div>
                    </button>
                    <button
                        type="button"
                        data-testid="print-layout-contact"
                        onClick={() => pick("contact")}
                        className="w-full text-left rounded-xl border border-white/10 bg-secondary/40 hover:bg-secondary/60 p-3 flex items-start gap-3 transition-colors"
                    >
                        <LayoutGrid size={18} className="text-primary mt-0.5" />
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-white">
                                Contact sheet
                            </p>
                            <p className="text-[12px] text-neutral-400 mt-0.5">
                                Compact 3-up grid. Best for trolley reference &
                                quick recall.
                            </p>
                        </div>
                    </button>
                </div>
                <button
                    type="button"
                    data-testid="print-layout-cancel"
                    onClick={() => onOpenChange(false)}
                    className="mt-3 mx-auto text-xs text-neutral-400 hover:text-neutral-200 underline underline-offset-4"
                >
                    Cancel
                </button>
            </DialogContent>
        </Dialog>
    );
}
